import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { X, Bell, ThumbsUp, Reply } from 'lucide-react';

export function AlertBanner({ alert, onDismiss, onConfirm, onReply }) {
  if (!alert || !alert.message) {
    return null;
  }

  // Detectar si es un mensaje de confirmación de lectura
  const isConfirmation = !!alert.message.includes('confirma la lectura del aviso');

  // Swipe to dismiss
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(null);
  const SWIPE_THRESHOLD = 80;

  const handlePointerDown = (e) => {
    if (e.target.closest('button')) return; // no iniciar swipe desde un botón
    startXRef.current = e.clientX;
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!isDragging || startXRef.current === null) return;
    const delta = e.clientX - startXRef.current;
    setDragX(Math.max(0, delta)); // solo hacia la derecha
  };

  const handlePointerUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    startXRef.current = null;
    if (dragX >= SWIPE_THRESHOLD) {
      onDismiss();
    } else {
      setDragX(0); // volver a posición original
    }
  };

  return (
    // Wrapper fixed con pointer-events-none (no intercepta clics)
    <div
      className="fixed left-0 right-0 z-[9999] px-3 pointer-events-none"
      style={{ top: '8px' }}
    >
      {/* Inner wrapper centrado y max-width */}
      <div
        className="mx-auto max-w-[520px] pointer-events-auto select-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        style={{
          transform: `translateX(${dragX}px)`,
          transition: isDragging ? 'none' : 'transform 200ms ease',
          touchAction: 'pan-y',
        }}
      >
        {/* Banner content */}
        <div className={isConfirmation
          ? "bg-green-50 border border-green-300 shadow-md rounded-xl overflow-hidden"
          : "bg-yellow-50 border border-yellow-300 shadow-md rounded-xl overflow-hidden"
        }>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            {/* Icono + Mensaje */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {isConfirmation
                ? <ThumbsUp className="h-5 w-5 text-green-600 flex-shrink-0" />
                : <Bell className="h-5 w-5 text-yellow-600 flex-shrink-0" />
              }
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium break-words ${isConfirmation ? 'text-green-900' : 'text-yellow-900'}`}>
                  {alert.message}
                </p>
                {(alert.createdByProfile || alert.createdByName) && (
                  <p className={`text-xs mt-1 ${isConfirmation ? 'text-green-800' : 'text-yellow-800'}`}>
                    Por: {alert.createdByProfile || alert.createdByName || 'Equipo'}
                  </p>
                )}
              </div>
            </div>

            {/* Botones de acción */}
            {onConfirm && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onConfirm}
                className="flex-shrink-0 h-8 w-8 p-0 rounded-full bg-green-100 hover:bg-green-200 text-green-700"
                aria-label="Confirmar lectura del aviso"
              >
                <ThumbsUp className="h-4 w-4" />
              </Button>
            )}
            {onReply && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onReply}
                className="flex-shrink-0 h-8 w-8 p-0 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600"
                aria-label="Responder aviso"
              >
                <Reply className="h-4 w-4" />
              </Button>
            )}
            {/* Botón Cerrar */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onDismiss}
              className="flex-shrink-0 h-8 w-8 p-0 rounded-full bg-stone-200 hover:bg-stone-300 text-stone-600"
              aria-label="Cerrar aviso"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
